import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ExportOptions = ({ onExport, pressKitData, elements }) => {
  const [activeExportType, setActiveExportType] = useState('web');
  const [exportSettings, setExportSettings] = useState({
    includeAnalytics: true,
    optimizeImages: true,
    generateSitemap: true,
    seoOptimized: true,
    customDomain: '',
    password: '',
    expirationDate: '',
    downloadTrackingEnabled: true
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Export format options
  const exportFormats = [
    {
      id: 'web',
      name: 'Responsive Website',
      icon: 'Globe',
      description: 'Mobile-optimized website with SEO features',
      features: ['Responsive Design', 'SEO Optimized', 'Fast Loading', 'Mobile Friendly'],
      color: 'bg-blue-500'
    },
    {
      id: 'pdf',
      name: 'PDF Document',
      icon: 'FileText',
      description: 'Print-ready PDF for media distribution',
      features: ['High Resolution', 'Print Ready', 'Downloadable', 'Professional Layout'],
      color: 'bg-red-500'
    },
    {
      id: 'wordpress',
      name: 'WordPress Plugin',
      icon: 'Code',
      description: 'Direct integration with WordPress sites',
      features: ['Easy Integration', 'Auto Updates', 'Widget Support', 'Theme Compatible'],
      color: 'bg-purple-500'
    },
    {
      id: 'email',
      name: 'Email Template',
      icon: 'Mail',
      description: 'HTML email template for campaigns',
      features: ['Email Optimized', 'Client Compatible', 'Responsive', 'Track Opens'],
      color: 'bg-green-500'
    },
    {
      id: 'social',
      name: 'Social Media Kit',
      icon: 'Share2',
      description: 'Optimized assets for social platforms',
      features: ['Platform Specific', 'Optimal Sizes', 'Brand Consistent', 'Engagement Ready'],
      color: 'bg-pink-500'
    },
    {
      id: 'api',
      name: 'API Integration',
      icon: 'Zap',
      description: 'REST API for custom implementations',
      features: ['JSON Format', 'Real-time Updates', 'Webhook Support', 'Developer Friendly'],
      color: 'bg-orange-500'
    }
  ];

  // Publishing platforms
  const publishingPlatforms = [
    { id: 'custom', name: 'Custom Domain', icon: 'Globe', url: 'your-domain.com' },
    { id: 'pengame', name: 'PenGame Hosting', icon: 'Server', url: 'pengamepr.com' },
    { id: 'github', name: 'GitHub Pages', icon: 'Github', url: 'github.io' },
    { id: 'netlify', name: 'Netlify', icon: 'Cloud', url: 'netlify.app' },
    { id: 'vercel', name: 'Vercel', icon: 'Triangle', url: 'vercel.app' }
  ];

  // Handle export process
  const handleExport = async () => {
    setIsExporting(true);
    setExportProgress(0);

    try {
      // Simulate export progress
      const steps = [
        'Preparing content...',
        'Optimizing images...',
        'Generating layout...',
        'Adding SEO metadata...',
        'Creating responsive styles...',
        'Finalizing export...'
      ];

      for (let i = 0; i < steps?.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 800));
        setExportProgress(((i + 1) / steps?.length) * 100);
      }

      await onExport?.(activeExportType, exportSettings);
      
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  // Handle setting change
  const handleSettingChange = (key, value) => {
    setExportSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const currentFormat = exportFormats?.find(format => format?.id === activeExportType);

  return (
    <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
      {/* Export Header */}
      <div className="p-4 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Export & Publish</h3>
        <p className="text-sm text-slate-600">
          Choose your preferred format and publishing options
        </p>
      </div>
      {/* Export Format Selection */}
      <div className="p-4 border-b border-slate-200">
        <h4 className="text-sm font-medium text-slate-900 mb-3">Export Format</h4>
        <div className="space-y-2">
          {exportFormats?.map((format) => (
            <div
              key={format?.id}
              onClick={() => setActiveExportType(format?.id)}
              className={`p-3 border rounded-lg cursor-pointer transition-all ${
                activeExportType === format?.id
                  ? 'border-indigo-500 bg-indigo-50' :'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 ${format?.color} rounded-lg flex items-center justify-center`}>
                  <Icon name={format?.icon} size={16} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="text-sm font-medium text-slate-900">{format?.name}</h5>
                  <p className="text-xs text-slate-600 mt-1">{format?.description}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {format?.features?.slice(0, 2)?.map((feature, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Export Settings */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 border-b border-slate-200">
          <h4 className="text-sm font-medium text-slate-900 mb-3">Export Settings</h4>
          
          {/* Format-specific settings */}
          {activeExportType === 'web' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">
                  Publishing Platform
                </label>
                <div className="space-y-2">
                  {publishingPlatforms?.map((platform) => (
                    <div key={platform?.id} className="flex items-center space-x-3 p-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                      <Icon name={platform?.icon} size={16} className="text-slate-600" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900">{platform?.name}</div>
                        <div className="text-xs text-slate-500">{platform?.url}</div>
                      </div>
                      <button className="text-xs text-indigo-600 hover:text-indigo-700">Select</button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Custom Domain (Optional)
                </label>
                <input
                  type="text"
                  placeholder="your-presskit.com"
                  value={exportSettings?.customDomain}
                  onChange={(e) => handleSettingChange('customDomain', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          )}

          {activeExportType === 'pdf' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Page Format
                </label>
                <select className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="a4">A4 (210 × 297 mm)</option>
                  <option value="letter">Letter (8.5 × 11 in)</option>
                  <option value="legal">Legal (8.5 × 14 in)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Quality
                </label>
                <select className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="high">High (Print Ready)</option>
                  <option value="medium">Medium (Web Optimized)</option>
                  <option value="low">Low (Fast Download)</option>
                </select>
              </div>
            </div>
          )}

          {/* Universal Settings */}
          <div className="space-y-3 mt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">SEO Optimized</span>
              <button
                onClick={() => handleSettingChange('seoOptimized', !exportSettings?.seoOptimized)}
                className={`w-10 h-6 rounded-full transition-colors ${
                  exportSettings?.seoOptimized ? 'bg-indigo-600' : 'bg-slate-300'
                }`}
              >
                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                  exportSettings?.seoOptimized ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">Optimize Images</span>
              <button
                onClick={() => handleSettingChange('optimizeImages', !exportSettings?.optimizeImages)}
                className={`w-10 h-6 rounded-full transition-colors ${
                  exportSettings?.optimizeImages ? 'bg-indigo-600' : 'bg-slate-300'
                }`}
              >
                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                  exportSettings?.optimizeImages ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">Include Analytics</span>
              <button
                onClick={() => handleSettingChange('includeAnalytics', !exportSettings?.includeAnalytics)}
                className={`w-10 h-6 rounded-full transition-colors ${
                  exportSettings?.includeAnalytics ? 'bg-indigo-600' : 'bg-slate-300'
                }`}
              >
                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                  exportSettings?.includeAnalytics ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="p-4 border-b border-slate-200">
          <h4 className="text-sm font-medium text-slate-900 mb-3">Security & Access</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Password Protection (Optional)
              </label>
              <input
                type="password"
                placeholder="Enter password"
                value={exportSettings?.password}
                onChange={(e) => handleSettingChange('password', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Expiration Date (Optional)
              </label>
              <input
                type="date"
                value={exportSettings?.expirationDate}
                onChange={(e) => handleSettingChange('expirationDate', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Export Preview */}
        <div className="p-4">
          <h4 className="text-sm font-medium text-slate-900 mb-3">Export Preview</h4>
          <div className="bg-slate-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-600">Format:</span>
              <span className="font-medium text-slate-900">{currentFormat?.name}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-600">Elements:</span>
              <span className="font-medium text-slate-900">{elements?.length} items</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-600">Estimated Size:</span>
              <span className="font-medium text-slate-900">2.4 MB</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-600">SEO Score:</span>
              <span className="font-medium text-green-600">92/100</span>
            </div>
          </div>
        </div>
      </div>
      {/* Export Actions */}
      <div className="p-4 border-t border-slate-200 space-y-3">
        {/* Export Progress */}
        {isExporting && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-600">Exporting...</span>
              <span className="text-xs font-medium text-slate-900">{Math.round(exportProgress)}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${exportProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Export Button */}
        <button
          onClick={handleExport}
          disabled={isExporting}
          className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Icon name={isExporting ? "Loader2" : currentFormat?.icon} size={16} className={isExporting ? "animate-spin" : ""} />
          <span className="font-medium">
            {isExporting ? 'Exporting...' : `Export as ${currentFormat?.name}`}
          </span>
        </button>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-2">
          <button className="flex items-center justify-center space-x-1 px-3 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm">
            <Icon name="Eye" size={14} />
            <span>Preview</span>
          </button>
          <button className="flex items-center justify-center space-x-1 px-3 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm">
            <Icon name="Clock" size={14} />
            <span>Schedule</span>
          </button>
        </div>

        {/* Export History */}
        <div className="pt-2 border-t border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-900">Recent Exports</span>
            <button className="text-xs text-indigo-600 hover:text-indigo-700">View All</button>
          </div>
          <div className="space-y-1">
            {[
              { format: 'PDF', date: '2 hours ago', size: '2.1 MB' },
              { format: 'Web', date: '1 day ago', size: '1.8 MB' }
            ]?.map((export_, index) => (
              <div key={index} className="flex items-center justify-between text-xs">
                <span className="text-slate-600">{export_?.format} • {export_?.date}</span>
                <span className="text-slate-500">{export_?.size}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportOptions;